import type { CalcInputs, CalcResult } from "./types";

/* ============================================================================
 *  STAWKI SKALIBROWANE NA REALNYCH OFERTACH PZU (kalkulator Dingo)
 * ----------------------------------------------------------------------------
 *  Krzywe składek dopasowano do 31 realnych propozycji klienckich PZU
 *  (Ochrona Każdego Dnia + Perspektywa na Przyszłość), folder ../../dane-pzu.
 *
 *  OCHRONA KAŻDEGO DNIA (sama śmierć): składka = (FIXED + R30(wiek)·suma/1000)·termAdj
 *    – kotwice (okres 30 lat): 37 l./100 tys.=69 zł, 33 l./200 tys.=91 zł,
 *      25 l./120 tys.=45 zł, 21 l./250 tys.=60 zł, 40 l./50 tys.=51 zł. Dopasowanie ~5%.
 *  PERSPEKTYWA: składka = suma/(okres·12)·loading(wiek)·wariant
 *    – kotwice: 48 l./50 tys./15 l.=354 zł, 40 l./100 tys./18 l.=567 zł, 59 l./50 tys./10 l.=560 zł.
 *
 *  Założenia BEZ danych (do uściślenia kolejnymi ofertami): wpływ okresu na składkę
 *  ochronną (termAdj – wszystkie oferty były na 30 lat), mnożnik palenia (1,4),
 *  mnożniki zakresu umów dodatkowych, składka dożywotniego Wsparcia Najbliższych
 *  (brak ofert – model whole-life kotwiczony na „od 125 zł").
 * ==========================================================================*/

/** Składka miesięczna (zł) za każde 1000 zł sumy — TYLKO ŚMIERĆ, okres 30 lat,
 *  osoba bez zwyżek, taryfa unisex. Interpolacja liniowa między punktami. */
const R30_PER_1000: Array<[age: number, rate: number]> = [
  [18, 0.13],
  [25, 0.22],
  [30, 0.31],
  [35, 0.43],
  [40, 0.61],
  [45, 0.88],
  [50, 1.26],
  [55, 1.81],
  [60, 2.6],
  [65, 3.74],
  [70, 5.4]
];

/** Stały składnik składki ochronnej (koszt obsługi) – z dopasowania do ofert. */
const FIXED_OCHRONA = 19;

export const RATES = {
  /** Ubezpieczenie ochronne terminowe (PZU Ochrona Każdego Dnia, CTIP56). */
  ochronne: {
    minMonthly: 45,
    smokerFactor: 1.4,
    /** mnożnik zakresu względem samej śmierci (z rozbicia składek w ofertach) */
    coverage: { smierc: 1.0, powazne: 1.45, pelny: 2.6 } as Record<string, number>,
    rangeSpread: { low: 0.9, high: 1.15 }
  },
  /** Ubezpieczenie pod kredyt hipoteczny (Ochrona Każdego Dnia z cesją). */
  kredyt: {
    minMonthly: 45,
    smokerFactor: 1.4,
    coverage: { smierc: 1.0, powazne: 1.45, niezdolnosc: 1.4 } as Record<string, number>,
    rangeSpread: { low: 0.9, high: 1.15 }
  },
  /** Pakiet zdrowotny (PZU Zdrowie). baseMonthly = realna roczna cena pakietu / 12. */
  zdrowotne: {
    zakres: { mini: 50, podstawowy: 124, optymalny: 254, premium: 453 } as Record<string, number>,
    // mnożniki wg realnych relacji cen pakietów PZU Zdrowie
    kto: { ja: 1.0, para: 1.7, rodzina: 2.6, dziecko: 1.7, senior: 1.05, student: 1.15 } as Record<string, number>
  },
  /** Na życie i dożycie z premiami (PZU Perspektywa na Przyszłość).
   *  składka = suma/(okres·12) · loading(wiek) · wariant. loading rośnie z wiekiem. */
  dozycie: {
    minMonthly: 200,
    loadingRefAge: 40,
    loadingBase: 1.22,
    loadingSlope: 0.00684,
    wariantFactor: { konserwatywny: 1.0, umiarkowany: 1.12 } as Record<string, number>,
    /** roczny przyrost wypłaty z premii ponad sumę gwarantowaną (scenariusz bazowy) */
    projectedGrowthPerYear: { konserwatywny: 0.021, umiarkowany: 0.032 } as Record<string, number>
  },
  /** Dożywotnie ubezpieczenie na życie (PZU Wsparcie Najbliższych, CBIP57).
   *  Brak realnych ofert — model whole-life kotwiczony na „od 125 zł/mies". */
  wsparcie: {
    minMonthly: 125,
    smokerFactor: 1.4,
    wholeLifeFactor: 2.4
  }
};

/* ----------------------------- helpers ----------------------------------- */

function num(inputs: CalcInputs, key: string, fallback = 0): number {
  const v = inputs[key];
  return typeof v === "number" ? v : typeof v === "string" ? Number(v) || fallback : fallback;
}
function str(inputs: CalcInputs, key: string, fallback = ""): string {
  const v = inputs[key];
  return typeof v === "string" ? v : fallback;
}
function bool(inputs: CalcInputs, key: string): boolean {
  return inputs[key] === true;
}
function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
function roundTo(value: number, step: number): number {
  return Math.round(value / step) * step;
}

/** Interpolowana stawka R30 (składka/1000 za samą śmierć, okres 30 lat). */
function r30(age: number): number {
  const pts = R30_PER_1000;
  const a = clamp(age, pts[0][0], pts[pts.length - 1][0]);
  for (let i = 0; i < pts.length - 1; i++) {
    const [a0, v0] = pts[i];
    const [a1, v1] = pts[i + 1];
    if (a <= a1) return v0 + (v1 - v0) * ((a - a0) / (a1 - a0));
  }
  return pts[pts.length - 1][1];
}

/** Mnożnik okresu ochrony względem 30 lat (założenie – brak danych dla krótszych). */
function termAdj(years: number): number {
  return years <= 5 ? 0.8 : years <= 10 ? 0.86 : years <= 15 ? 0.92 : years <= 20 ? 0.95 : years <= 25 ? 0.98 : 1.0;
}

/** Bazowa miesięczna składka za samą śmierć (Ochrona Każdego Dnia), okres 30 lat. */
function deathBase30(age: number, sum: number): number {
  return FIXED_OCHRONA + r30(age) * (sum / 1000);
}

/** Składka miesięczna umowy dodatkowej „Leczenie za granicą" (LZ) — REALNA tabela
 *  PZU (prezentacja 09.2025). [maxWiek, Standard, Premium]; dorosły do 64 lat. */
const LZ_ADULT: Array<[max: number, standard: number, premium: number]> = [
  [24, 68, 80],
  [29, 75, 88],
  [34, 82, 97],
  [39, 90, 107],
  [44, 99, 118],
  [49, 112, 133],
  [54, 126, 150],
  [59, 141, 168],
  [64, 158, 189]
];
function lzMonthly(age: number, variant: string): number {
  if (variant !== "standard" && variant !== "premium") return 0;
  const idx = variant === "premium" ? 2 : 1;
  for (const band of LZ_ADULT) if (age <= band[0]) return band[idx];
  return LZ_ADULT[LZ_ADULT.length - 1][idx];
}

const zl = (n: number) => `${Math.round(n).toLocaleString("pl-PL")} zł`;

/* ----------------------------- compute ----------------------------------- */

export function computeOchronne(inputs: CalcInputs): CalcResult {
  const r = RATES.ochronne;
  const age = num(inputs, "age", 35);
  const sum = num(inputs, "sum", 300000);
  const term = num(inputs, "term", 20);
  const smoker = bool(inputs, "smoker");
  const coverage = str(inputs, "coverage", "smierc");

  const lz = str(inputs, "lz", "bez");
  const base = Math.max(
    r.minMonthly,
    deathBase30(age, sum) * termAdj(term) * (r.coverage[coverage] ?? 1) * (smoker ? r.smokerFactor : 1)
  );
  const lzCost = lzMonthly(age, lz);
  const monthly = base + lzCost;

  const breakdown = [
    { label: "Suma ubezpieczenia", value: zl(sum) },
    { label: "Okres ochrony", value: `${term} lat` }
  ];
  if (lzCost > 0) {
    breakdown.push({
      label: `Leczenie za granicą (${lz === "premium" ? "Premium" : "Standard"})`,
      value: `+${zl(lzCost)} / mies.`
    });
  }
  return {
    amount: roundTo(monthly, 1),
    amountLabel: "Orientacyjna składka miesięczna",
    amountSuffix: "zł/mies.",
    rangeLow: roundTo(monthly * r.rangeSpread.low, 1),
    rangeHigh: roundTo(monthly * r.rangeSpread.high, 1),
    note: `Suma ubezpieczenia ${zl(sum)} na wypadek śmierci, ochrona przez ${term} lat${lzCost > 0 ? ", z umową leczenia onkologicznego za granicą" : ""}. Dokładną składkę doradca potwierdza po 2–3 pytaniach o zdrowie i styl życia.`,
    breakdown
  };
}

export function computeKredyt(inputs: CalcInputs): CalcResult {
  const r = RATES.kredyt;
  const age = num(inputs, "age", 35);
  const sum = num(inputs, "sum", 400000);
  const term = num(inputs, "term", 25);
  const smoker = bool(inputs, "smoker");
  const coverage = str(inputs, "coverage", "smierc");

  const monthly = Math.max(
    r.minMonthly,
    deathBase30(age, sum) * termAdj(term) * (r.coverage[coverage] ?? 1) * (smoker ? r.smokerFactor : 1)
  );

  return {
    amount: roundTo(monthly, 1),
    amountLabel: "Orientacyjna składka miesięczna",
    amountSuffix: "zł/mies.",
    rangeLow: roundTo(monthly * r.rangeSpread.low, 1),
    rangeHigh: roundTo(monthly * r.rangeSpread.high, 1),
    note: `Suma ubezpieczenia ${zl(sum)} (= saldo kredytu) na ${term} lat, z cesją na bank. Doradca dopasuje sumę i zakres do wymogów Twojego banku i potwierdzi dokładną składkę.`,
    breakdown: [
      { label: "Suma = kwota kredytu", value: zl(sum) },
      { label: "Okres = okres kredytu", value: `${term} lat` }
    ]
  };
}

export function computeZdrowotne(inputs: CalcInputs): CalcResult {
  const r = RATES.zdrowotne;
  const zakres = str(inputs, "zakres", "podstawowy");
  const kto = str(inputs, "kto", "ja");

  const base = r.zakres[zakres] ?? r.zakres.podstawowy;
  const monthly = Math.max(40, base * (r.kto[kto] ?? 1));
  const annual = monthly * 12;

  return {
    amount: roundTo(monthly, 1),
    amountLabel: "Orientacyjna składka miesięczna",
    amountSuffix: "zł/mies.",
    note: "Dostęp do prywatnej opieki medycznej (telekonsultacje, specjaliści, diagnostyka) w ponad 2400 placówkach. Pakiet zwykle opłaca się rocznie z góry. Dokładny pakiet i cenę potwierdzi doradca.",
    breakdown: [
      { label: "Płatność roczna", value: `${zl(annual)} / rok` },
      { label: "W pakiecie", value: "telekonsultacje, specjaliści, diagnostyka" }
    ]
  };
}

export function computeDozycie(inputs: CalcInputs): CalcResult {
  const r = RATES.dozycie;
  const age = num(inputs, "age", 40);
  const term = num(inputs, "term", 15);
  const sum = num(inputs, "sum", 50000);
  const wariant = str(inputs, "wariant", "konserwatywny");

  const loading = clamp(r.loadingBase + r.loadingSlope * (age - r.loadingRefAge), 1.12, 1.5);
  const monthly = Math.max(r.minMonthly, (sum / (term * 12)) * loading * (r.wariantFactor[wariant] ?? 1));
  const projected = sum * (1 + (r.projectedGrowthPerYear[wariant] ?? 0.021) * term);
  const paid = monthly * 12 * term;
  const wariantLabel = wariant === "umiarkowany" ? "Umiarkowany" : "Konserwatywny";

  return {
    amount: roundTo(monthly, 1),
    amountLabel: "Orientacyjna składka miesięczna",
    amountSuffix: "zł/mies.",
    note: `Suma gwarantowana ${zl(sum)} (wypłata dla bliskich w razie śmierci albo dla Ciebie po ${term} latach, wariant ${wariantLabel}). Z premiami szacunkowo ok. ${zl(projected)}. Dokładne wartości i ewentualny rabat potwierdzi doradca.`,
    breakdown: [
      { label: "Suma gwarantowana", value: zl(sum) },
      { label: "Szac. wypłata z premiami", value: zl(projected) },
      { label: "Łącznie wpłacisz", value: zl(paid) }
    ]
  };
}

export function computeWsparcie(inputs: CalcInputs): CalcResult {
  const r = RATES.wsparcie;
  const age = num(inputs, "age", 40);
  const sum = num(inputs, "sum", 150000);
  const smoker = bool(inputs, "smoker");

  // okres opłacania składki wg wieku wstępu (OWU CBIP57)
  const payUntilAge = age <= 45 ? 70 : age <= 55 ? 75 : age <= 60 ? 80 : age <= 65 ? 85 : age + 15;
  const payYears = age >= 66 ? 15 : Math.max(1, payUntilAge - age);

  const monthly = Math.max(
    r.minMonthly,
    deathBase30(age, sum) * r.wholeLifeFactor * (smoker ? r.smokerFactor : 1)
  );

  return {
    amount: roundTo(monthly, 1),
    amountLabel: "Orientacyjna składka miesięczna",
    amountSuffix: "zł/mies.",
    note: `Ochrona dożywotnia: suma ${zl(sum)} trafi do bliskich niezależnie od tego, kiedy nastąpi śmierć. Składki opłacasz ok. ${payYears} lat (do ${payUntilAge}. roku życia), a ochrona trwa do końca życia. Dokładną składkę potwierdzi doradca.`,
    breakdown: [
      { label: "Suma ubezpieczenia", value: zl(sum) },
      { label: "Składki opłacasz przez", value: `~${payYears} lat (do ${payUntilAge}. r.ż.)` },
      { label: "Ochrona", value: "dożywotnia (całe życie)" }
    ]
  };
}
